import React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { ChevronDown, Maximize2, Minimize2, X } from '@lifeomic/chromicons';
import { useIntl } from 'react-intl';

import { ChatTab } from './ChatTab';
import { ChatDockTabTitle } from './ChatDockRow';
import {
  ConversationEntry,
  ensurePopulationConversationId,
  FhirSubjectSearch,
  resolvePatientIdFromConversation,
} from './chatDockUtils';
import {
  CLINICIANS_AGENT_USERNAME,
  POPULATION_AGENT_USERNAME,
} from '../constants';
import { chatDockMessages } from './chatDockMessages';

type ChatDockTabClasses = {
  tabContainer: string;
  tabContainerCollapsed: string;
  tabContainerFullscreen: string;
  tabHeader: string;
  tabTitle: string;
  tabActions: string;
  chatContainer: string;
  emptyState: string;
};

export const ChatDockTab = ({
  conversation,
  index,
  chatGap,
  chatListWidth,
  chatTabWidth,
  classes,
  maskedMode,
  fhirSubjectSearch,
  activeProjectId,
  activeProjectCohortId,
  fullscreenKey,
  collapsedKeys,
  setCollapsedKeys,
  setFullscreenKey,
  setOpenConversationKeys,
  setLocalConversationMap,
  hasFullscreenConversation,
}: {
  conversation: ConversationEntry;
  index: number;
  chatGap: number;
  chatListWidth: number;
  chatTabWidth: number;
  classes: ChatDockTabClasses;
  maskedMode: boolean;
  fhirSubjectSearch: FhirSubjectSearch;
  activeProjectId: string;
  activeProjectCohortId?: string;
  fullscreenKey: string | null;
  collapsedKeys: string[];
  setCollapsedKeys: React.Dispatch<React.SetStateAction<string[]>>;
  setFullscreenKey: React.Dispatch<React.SetStateAction<string | null>>;
  setOpenConversationKeys: React.Dispatch<React.SetStateAction<string[]>>;
  setLocalConversationMap: React.Dispatch<
    React.SetStateAction<Record<string, ConversationEntry>>
  >;
  hasFullscreenConversation: boolean;
}) => {
  const { formatMessage } = useIntl();
  const isFullscreen = fullscreenKey === conversation.key;
  const isCollapsed = collapsedKeys.includes(conversation.key);
  if (hasFullscreenConversation && !isFullscreen) {
    return null;
  }

  const resolvedPatientId = resolvePatientIdFromConversation(conversation);
  const agentUsername = conversation.userIds?.includes(
    POPULATION_AGENT_USERNAME,
  )
    ? POPULATION_AGENT_USERNAME
    : CLINICIANS_AGENT_USERNAME;
  const rightOffset =
    chatGap + chatListWidth + chatGap + index * (chatTabWidth + chatGap);

  return (
    <Box
      key={conversation.key}
      className={`${classes.tabContainer} ${
        isFullscreen ? classes.tabContainerFullscreen : ''
      } ${isCollapsed ? classes.tabContainerCollapsed : ''}`}
      style={!isFullscreen ? { right: rightOffset } : undefined}
    >
      <Box
        className={classes.tabHeader}
        onClick={() => {
          if (!isCollapsed) return;
          setCollapsedKeys(keys =>
            keys.filter(key => key !== conversation.key),
          );
        }}
      >
        <Box className={classes.tabTitle}>
          <ChatDockTabTitle
            conversation={conversation}
            maskedMode={maskedMode}
            fhirSubjectSearch={fhirSubjectSearch}
            activeProjectId={activeProjectId}
            activeProjectCohortId={activeProjectCohortId}
          />
        </Box>
        {!isCollapsed && (
          <Box className={classes.tabActions}>
            <IconButton
              aria-label="Minimize chat"
              icon={ChevronDown}
              size={0}
              onClick={() =>
                setCollapsedKeys(keys =>
                  keys.includes(conversation.key)
                    ? keys.filter(key => key !== conversation.key)
                    : [...keys, conversation.key],
                )
              }
            />
            <IconButton
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
              icon={isFullscreen ? Minimize2 : Maximize2}
              size={0}
              onClick={event => {
                event.stopPropagation();
                const next = isFullscreen ? null : conversation.key;
                setFullscreenKey(next);
                if (next) {
                  setCollapsedKeys(keys =>
                    keys.filter(key => key !== conversation.key),
                  );
                }
              }}
            />
            <IconButton
              aria-label={formatMessage(chatDockMessages.close)}
              icon={X}
              size={0}
              onClick={event => {
                event.stopPropagation();
                setOpenConversationKeys(keys =>
                  keys.filter(key => key !== conversation.key),
                );
                setLocalConversationMap(prev => {
                  if (!prev[conversation.key]) return prev;
                  const next = { ...prev };
                  delete next[conversation.key];
                  return next;
                });
                setFullscreenKey(prev =>
                  prev === conversation.key ? null : prev,
                );
                setCollapsedKeys(keys =>
                  keys.filter(key => key !== conversation.key),
                );
              }}
            />
          </Box>
        )}
      </Box>
      {!isCollapsed && (
        <Box className={classes.chatContainer}>
          {resolvedPatientId ? (
            <ChatTab
              patientId={resolvedPatientId}
              agentUsername={agentUsername}
            />
          ) : agentUsername === POPULATION_AGENT_USERNAME ? (
            <ChatTab
              agentUsername={agentUsername}
              allowMissingPatient={true}
              userIdsOverride={ensurePopulationConversationId(
                conversation.userIds,
              )}
            />
          ) : (
            <Box
              align="center"
              justify="center"
              fullHeight
              fullWidth
              className={classes.emptyState}
            >
              <Text size="subbody">
                {formatMessage(chatDockMessages.missingPatient)}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
